import { useState, useEffect, useCallback } from 'react';
import { MainLayout } from '../components/layout/Sidebar';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ScrollArea } from '../components/ui/scroll-area';
import { auditApi } from '../lib/api';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import {
  Shield, Activity, Users, Clock, Filter, ChevronLeft, ChevronRight,
  Plus, Trash2, ArrowRightLeft, RefreshCw, CheckCircle2, UserPlus,
  Search, AlertCircle, FileText, Calendar, Loader2
} from 'lucide-react';

const ACTION_CONFIG = {
  create: { icon: Plus, color: '#22c55e', label: 'Created' },
  delete: { icon: Trash2, color: '#ef4444', label: 'Deleted' },
  bulk_delete: { icon: Trash2, color: '#ef4444', label: 'Bulk Deleted' },
  assign: { icon: UserPlus, color: '#3b82f6', label: 'Assigned' },
  transfer: { icon: ArrowRightLeft, color: '#f59e0b', label: 'Transferred' },
  update: { icon: RefreshCw, color: '#8b5cf6', label: 'Updated' },
  complete: { icon: CheckCircle2, color: '#10b981', label: 'Completed' },
  login: { icon: Activity, color: '#06b6d4', label: 'Login' },
  logout: { icon: Activity, color: '#64748b', label: 'Logout' },
  register: { icon: UserPlus, color: '#22c55e', label: 'Registered' },
  deactivate: { icon: AlertCircle, color: '#f97316', label: 'Deactivated' },
  checkin: { icon: CheckCircle2, color: '#10b981', label: 'Check-In' },
  checkout: { icon: CheckCircle2, color: '#0ea5e9', label: 'Check-Out' },
};

function StatCard({ title, value, icon: Icon, color }) {
  return (
    <Card className="border-gray-200" data-testid={`audit-stat-${title.toLowerCase().replace(/\s/g, '-')}`}>
      <CardContent className="p-4 flex items-center gap-4">
        <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${color}15` }}>
          <Icon className="w-5 h-5" style={{ color }} />
        </div>
        <div>
          <p className="text-2xl font-bold text-gray-900">{value}</p>
          <p className="text-xs text-gray-500">{title}</p>
        </div>
      </CardContent>
    </Card>
  );
}

function LogRow({ log, index }) {
  const config = ACTION_CONFIG[log.action] || { icon: Activity, color: '#94a3b8', label: log.action };
  const Icon = config.icon;
  const ts = new Date(log.timestamp);
  const timeStr = ts.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
  const dateStr = ts.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });

  return (
    <motion.div
      className="flex items-start gap-3 px-4 py-3 border-b border-gray-100 last:border-0 hover:bg-gray-50/50 transition-colors"
      initial={{ opacity: 0, x: -10 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ delay: index * 0.03 }}
      data-testid={`audit-log-${log.id}`}
    >
      <div className="w-8 h-8 rounded-md flex items-center justify-center flex-shrink-0 mt-0.5" style={{ backgroundColor: `${config.color}15` }}>
        <Icon className="w-4 h-4" style={{ color: config.color }} />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm text-gray-800 leading-snug">{log.detail}</p>
        <div className="flex items-center gap-2 mt-1 flex-wrap">
          <Badge variant="outline" className="text-xs font-mono px-1.5 py-0" style={{ color: config.color, borderColor: `${config.color}40` }}>
            {config.label}
          </Badge>
          <span className="text-xs text-gray-400">{log.module}</span>
          <span className="text-xs text-gray-400 hidden sm:inline">by {log.user_name}</span>
        </div>
      </div>
      <div className="text-right flex-shrink-0">
        <p className="text-xs font-mono text-gray-500">{timeStr}</p>
        <p className="text-xs text-gray-400">{dateStr}</p>
      </div>
    </motion.div>
  );
}

export default function AuditLogsPage() {
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState(null);
  const [filterOptions, setFilterOptions] = useState({ actions: [], modules: [] });
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const pageSize = 30;

  // Filters
  const [actionFilter, setActionFilter] = useState('');
  const [moduleFilter, setModuleFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: pageSize, skip: page * pageSize };
      if (actionFilter) params.action = actionFilter;
      if (moduleFilter) params.module = moduleFilter;
      const res = await auditApi.getLogs(params);
      setLogs(res.data.logs || []);
      setTotal(res.data.total || 0);
    } catch (err) {
      toast.error('Failed to fetch audit logs');
    } finally {
      setLoading(false);
    }
  }, [page, actionFilter, moduleFilter]);

  const fetchStats = useCallback(async () => {
    try {
      const res = await auditApi.getStats();
      setStats(res.data);
    } catch {
      // Stats are non-critical
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchFilterOptions = useCallback(async () => {
    try {
      const res = await auditApi.getActions();
      setFilterOptions(res.data);
    } catch {
      // Filters are non-critical
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  useEffect(() => {
    fetchStats();
    fetchFilterOptions();
  }, [fetchStats, fetchFilterOptions]);

  const filteredLogs = searchQuery
    ? logs.filter(l =>
        l.detail?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        l.user_name?.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : logs;

  const totalPages = Math.ceil(total / pageSize);

  return (
    <MainLayout>
      <div className="space-y-6" data-testid="audit-logs-page">
        {/* Header */}
        <motion.div
          className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3"
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-amber-500/10 border border-amber-500/20 flex items-center justify-center">
              <Shield className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <h1 className="text-xl font-semibold text-gray-900">Audit Logs</h1>
              <p className="text-sm text-gray-500">Track all platform activity</p>
            </div>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => { fetchLogs(); fetchStats(); }}
            className="gap-1.5"
            data-testid="audit-refresh-btn"
          >
            <RefreshCw className="w-3.5 h-3.5" />
            Refresh
          </Button>
        </motion.div>

        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <StatCard title="Total Events" value={stats.total || 0} icon={Activity} color="#3b82f6" />
            <StatCard title="Today" value={stats.today || 0} icon={Clock} color="#22c55e" />
            <StatCard title="Actions Types" value={Object.keys(stats.by_action || {}).length} icon={FileText} color="#8b5cf6" />
            <StatCard title="Active Users" value={stats.active_users?.length || 0} icon={Users} color="#f59e0b" />
          </div>
        )}

        {/* Filters */}
        <Card className="border-gray-200">
          <CardContent className="p-3 flex flex-wrap items-center gap-2">
            <Filter className="w-4 h-4 text-gray-400 flex-shrink-0" />
            <div className="relative flex-1 min-w-[150px] max-w-xs">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
              <Input
                placeholder="Search logs..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-8 h-8 text-sm"
                data-testid="audit-search-input"
              />
            </div>
            <Select value={actionFilter} onValueChange={(v) => { setActionFilter(v === 'all' ? '' : v); setPage(0); }}>
              <SelectTrigger className="w-[140px] h-8 text-sm" data-testid="audit-action-filter">
                <SelectValue placeholder="All Actions" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Actions</SelectItem>
                {filterOptions.actions.map(a => (
                  <SelectItem key={a} value={a}>{a}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={moduleFilter} onValueChange={(v) => { setModuleFilter(v === 'all' ? '' : v); setPage(0); }}>
              <SelectTrigger className="w-[140px] h-8 text-sm" data-testid="audit-module-filter">
                <SelectValue placeholder="All Modules" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Modules</SelectItem>
                {filterOptions.modules.map(m => (
                  <SelectItem key={m} value={m}>{m}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </CardContent>
        </Card>

        {/* Logs Table */}
        <Card className="border-gray-200">
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center py-16 gap-2 text-gray-400">
                <Loader2 className="w-5 h-5 animate-spin" />
                <span className="text-sm">Loading audit logs...</span>
              </div>
            ) : filteredLogs.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 text-gray-400" data-testid="audit-empty-state">
                <AlertCircle className="w-10 h-10 mb-3 opacity-40" />
                <p className="text-sm font-medium">No audit logs found</p>
                <p className="text-xs mt-1">Activity will appear here as actions are performed</p>
              </div>
            ) : (
              <ScrollArea className="max-h-[500px]">
                {filteredLogs.map((log, i) => (
                  <LogRow key={log.id} log={log} index={i} />
                ))}
              </ScrollArea>
            )}
          </CardContent>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100">
              <p className="text-xs text-gray-500">
                Showing {page * pageSize + 1}-{Math.min((page + 1) * pageSize, total)} of {total}
              </p>
              <div className="flex items-center gap-1">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page === 0}
                  onClick={() => setPage(p => p - 1)}
                  className="h-7 w-7 p-0"
                  data-testid="audit-prev-page"
                >
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <span className="text-xs text-gray-500 px-2">{page + 1}/{totalPages}</span>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page >= totalPages - 1}
                  onClick={() => setPage(p => p + 1)}
                  className="h-7 w-7 p-0"
                  data-testid="audit-next-page"
                >
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}
        </Card>
      </div>
    </MainLayout>
  );
}
